import React from "react";
import { map } from "lodash";
import styled, { css } from "styled-components";
import {
  Radius,
  Spacing,
  content,
  prettyScrollbars,
  transparentize,
} from "@glossy";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import Image from "@ui-kit/utils/image";
import { ClusterPost } from "@radarly/models/posts";

export interface IProps {
  pictures: ClusterPost[];
  display: "row" | "grid";
  onClickMore: () => void;
  renderTooltip: (data: ClusterPost) => React.ReactNode;
}

const Root = styled.div<{ $display: IProps["display"] }>`
  position: relative;
  cursor: pointer;
  ${({ $display }) =>
    $display === "grid"
      ? css`
          width: 100%;
          height: 100%;
          overflow: auto;
          ${prettyScrollbars};
        `
      : css`
          border-radius: ${Radius.Medium};
          padding: ${Spacing.Medium};

          :hover {
            background-color: ${(props) =>
              transparentize(0.85, content(props, "primary"))};
          }
        `}
`;

const Pictures = styled.div<{ $display: IProps["display"] }>`
  display: ${({ $display }) => ($display === "row" ? "flex" : "grid")};
  gap: ${Spacing.Medium};
  overflow: hidden;
  ${({ $display }) =>
    $display === "grid"
      ? css`
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          grid-gap: 12px;
          overflow: hidden;
          justify-content: space-between;
        `
      : css`
          mask-image: linear-gradient(
            to left,
            rgba(0, 0, 0, 0) 0,
            rgba(0, 0, 0, 1) 100px
          );
        `}
`;

const ImageWrapper = styled.div<{ $display: IProps["display"] }>`
  position: relative;
  overflow: hidden;
  border-radius: ${Radius.Small};
  background-color: ${(props) => content(props, "tertiary")};
  color: ${(props) => content(props, "secondary")};
  ${({ $display }) =>
    $display === "grid"
      ? css`
          display: grid;
          place-items: center;
          width: 100%;
          height: 100%;

          &::before {
            // for aspect ratio
            content: "";
            display: block;
            padding-bottom: 100%;
            grid-area: 1 / 1 / 2 / 2;
          }
        `
      : css`
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100px;
          width: 100px;
        `}

  & > * {
    position: absolute;
  }
`;

const Chevron = styled(Icon)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => content(props, "secondary")};
  cursor: pointer;
`;

const TooltipWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

const RadarlyImageClusteringPictures = ({
  pictures,
  display,
  onClickMore,
  renderTooltip,
}: IProps) => (
  <Root $display={display} onClick={onClickMore}>
    <Pictures $display={display}>
      {map(pictures, (picture, key) => (
        <TooltipWrapper
          condition={!!renderTooltip}
          wrapper={(children) => (
            <Tooltip tooltip={renderTooltip(picture)} key={key}>
              {children}
            </Tooltip>
          )}
          key={key}
        >
          <ImageWrapper
            key={key}
            $display={display}
            onClick={() =>
              display === "grid" && window.open(picture.permalink, "_blank")
            }
          >
            <Image
              key={key}
              image={picture.mediaUrl}
              fallbackImage={picture.srcFallback}
              lazy
              animated
            />
          </ImageWrapper>
        </TooltipWrapper>
      ))}
    </Pictures>
    {display === "row" && (
      <Chevron glyph="chevron-right" size="ms" onClick={onClickMore} />
    )}
  </Root>
);

export default RadarlyImageClusteringPictures;
